/*

? A listing's title after its location pin is moved (decided 3 Nov 2026), one rule for the
? website and both apps.

The anchor is the place the title belongs to: the suggestion the user picked, or the listing
as it was when the location edit opened. While the pin stays within PIN_RETITLE_DISTANCE_METRES
of the anchor the user is only fine-tuning the spot (the right gate, the right building), so
the title stays the anchor's. Moved further, the pin is somewhere else, and the title becomes
the pin's own area (sublocality, else locality, worked out by the caller's geocoding). The
coordinates, country, state and city always follow the pin.

Used by `lib/GooglePlaces.ts` (`reverseGeocode`, behind the apps' `bbm-android/places/reverse-geocode`)
and by the website's Create Property (`PropertyLocation.tsx`) and property page (`MapCard.tsx`),
which geocode in the browser. Pure: no imports, safe in the browser bundle.

*/

/** How far the pin may move from the anchor before the title follows the pin. */
export const PIN_RETITLE_DISTANCE_METRES = 500;

/** The place a listing's title belongs to. */
export interface PinAnchor {
	latitude: number;
	longitude: number;
	title: string;
}

/** Great-circle distance in metres. */
export function metresBetween(aLatitude: number, aLongitude: number, bLatitude: number, bLongitude: number): number {
	const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
	const dLatitude = toRadians(bLatitude - aLatitude);
	const dLongitude = toRadians(bLongitude - aLongitude);
	const h =
		Math.sin(dLatitude / 2) ** 2 +
		Math.cos(toRadians(aLatitude)) * Math.cos(toRadians(bLatitude)) * Math.sin(dLongitude / 2) ** 2;
	return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * The listing's title with its pin at [pinLatitude, pinLongitude].
 *
 * @param anchor       the picked place or the listing as opened; null when there is none.
 * @param pinAreaTitle the pin's own area from geocoding; "" when unknown.
 */
export function pinTitle(
	anchor: PinAnchor | null | undefined,
	pinLatitude: number,
	pinLongitude: number,
	pinAreaTitle: string,
): string {
	const anchorTitle = (anchor?.title || "").trim();
	const areaTitle = (pinAreaTitle || "").trim();
	const hasAnchor =
		!!anchor && anchorTitle !== "" && Number.isFinite(anchor.latitude) && Number.isFinite(anchor.longitude);
	if (
		hasAnchor &&
		metresBetween(anchor!.latitude, anchor!.longitude, pinLatitude, pinLongitude) <= PIN_RETITLE_DISTANCE_METRES
	) {
		return anchorTitle;
	}
	/* Moved away (or no anchor): the pin's area; when that is unknown, the old title stays. */
	return areaTitle || anchorTitle;
}
